import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Layout, Menu, Typography, Button, Tag, Space, Select } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  FunnelPlotOutlined,
  HistoryOutlined,
  FileTextOutlined,
  BarChartOutlined,
  NotificationOutlined,
  CustomerServiceOutlined,
  ShoppingOutlined,
  ApartmentOutlined,
  ShareAltOutlined,
  ContactsOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  AppstoreOutlined,
  DollarOutlined,
  AuditOutlined,
  CrownOutlined,
  BellOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  FolderOutlined,
  MobileOutlined,
  CommentOutlined,
  DatabaseOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient, { type ApiResponse } from './api/client';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { EXTERNAL_PORTALS } from './config/externalPortals';
import CustomerListPage from './pages/customers/CustomerListPage';
import CustomerDetailPage from './pages/customers/CustomerDetailPage';
import CustomerForm from './pages/customers/CustomerForm';
import ContactListPage from './pages/contacts/ContactListPage';
import ContactDetailPage from './pages/contacts/ContactDetailPage';
import ContactForm from './pages/contacts/ContactForm';
import PipelineListPage from './pages/pipelines/PipelineListPage';
import PipelineForm from './pages/pipelines/PipelineForm';
import OpportunityListPage from './pages/opportunities/OpportunityListPage';
import OpportunityKanbanPage from './pages/opportunities/OpportunityKanbanPage';
import OpportunityDetailPage from './pages/opportunities/OpportunityDetailPage';
import OpportunityForm from './pages/opportunities/OpportunityForm';
import ActivityListPage from './pages/activities/ActivityListPage';
import ActivityDetailPage from './pages/activities/ActivityDetailPage';
import ActivityForm from './pages/activities/ActivityForm';
import QuoteListPage from './pages/quotes/QuoteListPage';
import QuoteDetailPage from './pages/quotes/QuoteDetailPage';
import QuoteForm from './pages/quotes/QuoteForm';
import OrderListPage from './pages/orders/OrderListPage';
import OrderDetailPage from './pages/orders/OrderDetailPage';
import OrderForm from './pages/orders/OrderForm';
import DashboardListPage from './pages/dashboards/DashboardListPage';
import DashboardViewPage from './pages/dashboards/DashboardViewPage';
import DashboardForm from './pages/dashboards/DashboardForm';
import ReportListPage from './pages/reports/ReportListPage';
import ReportDetailPage from './pages/reports/ReportDetailPage';
import ReportForm from './pages/reports/ReportForm';
import CampaignListPage from './pages/marketing/CampaignListPage';
import CampaignDetailPage from './pages/marketing/CampaignDetailPage';
import CampaignForm from './pages/marketing/CampaignForm';
import CampaignAnalyticsDashboard from './pages/marketing/CampaignAnalyticsDashboard';
import TicketListPage from './pages/tickets/TicketListPage';
import TicketDetailPage from './pages/tickets/TicketDetailPage';
import TicketForm from './pages/tickets/TicketForm';
import ConversationManagementPage from './pages/conversations/ConversationManagementPage';
import MemberMessageManagementPage from './pages/member-messages/MemberMessageManagementPage';
import ProductListPage from './pages/products/ProductListPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import ProductForm from './pages/products/ProductForm';
import InventoryTransactionForm from './pages/inventory/InventoryTransactionForm';
import WorkflowDefinitionListPage from './pages/workflows/WorkflowDefinitionListPage';
import WorkflowDefinitionDetailPage from './pages/workflows/WorkflowDefinitionDetailPage';
import WorkflowDefinitionForm from './pages/workflows/WorkflowDefinitionForm';
import WorkflowInstanceListPage from './pages/workflows/WorkflowInstanceListPage';
import WorkflowInstanceDetailPage from './pages/workflows/WorkflowInstanceDetailPage';
import WorkflowInstanceForm from './pages/workflows/WorkflowInstanceForm';
import SocialAccountListPage from './pages/social/SocialAccountListPage';
import SocialAccountDetailPage from './pages/social/SocialAccountDetailPage';
import SocialAccountForm from './pages/social/SocialAccountForm';
import SocialPostListPage from './pages/social/SocialPostListPage';
import SocialPostDetailPage from './pages/social/SocialPostDetailPage';
import SocialPostForm from './pages/social/SocialPostForm';
import SocialMessageListPage from './pages/social/SocialMessageListPage';
import SocialMessageDetailPage from './pages/social/SocialMessageDetailPage';
import AuditLogListPage from './pages/audit/AuditLogListPage';
import AuditLogDetailPage from './pages/audit/AuditLogDetailPage';
import AuditConfigListPage from './pages/audit/AuditConfigListPage';
import AuditConfigForm from './pages/audit/AuditConfigForm';
import AdminDashboard from './pages/admin/AdminDashboard';
import TenantListPage from './pages/admin/TenantListPage';
import TenantDetailPage from './pages/admin/TenantDetailPage';
import TenantForm from './pages/admin/TenantForm';
import SystemSettingListPage from './pages/admin/SystemSettingListPage';
import SystemSettingForm from './pages/admin/SystemSettingForm';
import TenantSettingListPage from './pages/admin/TenantSettingListPage';
import TenantSettingForm from './pages/admin/TenantSettingForm';
import NotificationPrefListPage from './pages/admin/NotificationPrefListPage';
import NotificationPrefForm from './pages/admin/NotificationPrefForm';
import BrandingPage from './pages/admin/BrandingPage';
import DomainListPage from './pages/admin/DomainListPage';
import DomainForm from './pages/admin/DomainForm';
import EmailTemplateListPage from './pages/admin/EmailTemplateListPage';
import EmailTemplateForm from './pages/admin/EmailTemplateForm';
import EmailTemplatePreview from './pages/admin/EmailTemplatePreview';
import NotificationListPage from './pages/notifications/NotificationListPage';
import NotificationDetailPage from './pages/notifications/NotificationDetailPage';
import ChannelConfigListPage from './pages/admin/ChannelConfigListPage';
import ChannelConfigForm from './pages/admin/ChannelConfigForm';
import IntegrationConfigListPage from './pages/integration/IntegrationConfigListPage';
import IntegrationConfigForm from './pages/integration/IntegrationConfigForm';
import OAuthTokenListPage from './pages/integration/OAuthTokenListPage';
import WebhookSubscriptionListPage from './pages/integration/WebhookSubscriptionListPage';
import WebhookSubscriptionForm from './pages/integration/WebhookSubscriptionForm';
import WebhookEventListPage from './pages/integration/WebhookEventListPage';
import ScoringRuleListPage from './pages/analytics/ScoringRuleListPage';
import ScoringRuleForm from './pages/analytics/ScoringRuleForm';
import LeadScoreListPage from './pages/analytics/LeadScoreListPage';
import SnapshotListPage from './pages/analytics/SnapshotListPage';
import ForecastListPage from './pages/analytics/ForecastListPage';
import FileListPage from './pages/files/FileListPage';
import FileShareListPage from './pages/files/FileShareListPage';
import MobileDeviceListPage from './pages/mobile/MobileDeviceListPage';
import MobileDashboardPage from './pages/mobile/MobileDashboardPage';
import TeamListPage from './pages/collaboration/TeamListPage';
import TeamDetailPage from './pages/collaboration/TeamDetailPage';
import TeamForm from './pages/collaboration/TeamForm';
import SharedViewListPage from './pages/collaboration/SharedViewListPage';
import SharedViewForm from './pages/collaboration/SharedViewForm';
import ImportJobListPage from './pages/data-management/ImportJobListPage';
import ImportJobDetailPage from './pages/data-management/ImportJobDetailPage';
import ImportJobForm from './pages/data-management/ImportJobForm';
import ExportJobListPage from './pages/data-management/ExportJobListPage';
import ExportJobDetailPage from './pages/data-management/ExportJobDetailPage';
import ExportJobForm from './pages/data-management/ExportJobForm';
import DeduplicationRuleListPage from './pages/data-management/DeduplicationRuleListPage';
import DeduplicationRuleDetailPage from './pages/data-management/DeduplicationRuleDetailPage';
import DeduplicationRuleForm from './pages/data-management/DeduplicationRuleForm';
import DeduplicationJobListPage from './pages/data-management/DeduplicationJobListPage';
import DeduplicationJobDetailPage from './pages/data-management/DeduplicationJobDetailPage';
import ExternalRedirectPage from './pages/external/ExternalRedirectPage';
import AuthCallbackPage from './pages/external/AuthCallbackPage';

const { Header, Sider, Content, Footer } = Layout;
const { Title, Text } = Typography;

type HealthStatus = 'checking' | 'healthy' | 'unhealthy';

function DashboardPage() {
  const { t } = useTranslation();
  const [apiHealth, setApiHealth] = useState<HealthStatus>('checking');
  const [esHealth, setEsHealth] = useState<HealthStatus>('checking');

  const checkHealth = async () => {
    setApiHealth('checking');
    setEsHealth('checking');
    try {
      const response = await apiClient.get<ApiResponse<{
        components: { api: string; elasticsearch: string };
      }>>('/health');
      const data = response.data;
      setApiHealth(data.success ? 'healthy' : 'unhealthy');
      setEsHealth(
        data.data?.components?.elasticsearch === 'healthy' ? 'healthy' : 'unhealthy'
      );
    } catch {
      setApiHealth('unhealthy');
      setEsHealth('unhealthy');
    }
  };

  useEffect(() => {
    checkHealth();
  }, []);

  const statusIcon = (status: HealthStatus) => {
    if (status === 'checking') return <LoadingOutlined />;
    if (status === 'healthy') return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
  };

  const statusColor = (status: HealthStatus) => {
    if (status === 'checking') return 'processing';
    if (status === 'healthy') return 'success';
    return 'error';
  };

  return (
    <div>
      <Title level={3}>{t('health.title')}</Title>
      <Space direction="vertical" size="middle">
        <Space>
          {statusIcon(apiHealth)}
          <Text>{t('health.api')}:</Text>
          <Tag color={statusColor(apiHealth)}>
            {t(`health.${apiHealth === 'checking' ? 'checking' : apiHealth}`)}
          </Tag>
        </Space>
        <Space>
          {statusIcon(esHealth)}
          <Text>{t('health.elasticsearch')}:</Text>
          <Tag color={statusColor(esHealth)}>
            {t(`health.${esHealth === 'checking' ? 'checking' : esHealth}`)}
          </Tag>
        </Space>
        <Button type="primary" onClick={checkHealth}>
          {t('health.check_button')}
        </Button>
      </Space>
    </div>
  );
}

const NAV_ITEMS = [
  { key: '/', icon: <DashboardOutlined />, labelKey: 'nav.dashboard' },
  { key: '/customers', icon: <TeamOutlined />, labelKey: 'nav.customers', permission: { module: 'customers', action: 'read' } },
  { key: '/contacts', icon: <ContactsOutlined />, labelKey: 'nav.contacts', permission: { module: 'contacts', action: 'read' } },
  { key: '/opportunities', icon: <DollarOutlined />, labelKey: 'nav.opportunities', permission: { module: 'opportunities', action: 'read' } },
  { key: '/pipelines', icon: <FunnelPlotOutlined />, labelKey: 'nav.pipelines', permission: { module: 'pipelines', action: 'read' } },
  { key: '/activities', icon: <HistoryOutlined />, labelKey: 'nav.activities', permission: { module: 'activities', action: 'read' } },
  { key: '/quotes', icon: <FileTextOutlined />, labelKey: 'nav.quotes', permission: { module: 'quotes', action: 'read' } },
  { key: '/orders', icon: <ShoppingOutlined />, labelKey: 'nav.orders', permission: { module: 'orders', action: 'read' } },
  { key: '/dashboards', icon: <AppstoreOutlined />, labelKey: 'nav.dashboards', permission: { module: 'dashboards', action: 'read' } },
  { key: '/reports', icon: <BarChartOutlined />, labelKey: 'nav.reports', permission: { module: 'reports', action: 'read' } },
  { key: '/marketing', icon: <NotificationOutlined />, labelKey: 'nav.marketing', permission: { module: 'marketing', action: 'read' } },
  { key: '/tickets', icon: <CustomerServiceOutlined />, labelKey: 'nav.tickets', permission: { module: 'tickets', action: 'read' } },
  { key: '/conversations', icon: <CommentOutlined />, labelKey: 'nav.conversations', permission: { module: 'conversations', action: 'read' } },
  {
    key: '/member-messages',
    icon: <MailOutlined />,
    labelKey: 'nav.member_messages',
    permission: { module: 'membermessages', action: 'read' },
  },
  { key: '/products', icon: <ShoppingOutlined />, labelKey: 'nav.products', permission: { module: 'products', action: 'read' } },
  { key: '/workflows', icon: <ApartmentOutlined />, labelKey: 'nav.workflows', permission: { module: 'workflows', action: 'read' } },
  { key: '/social', icon: <ShareAltOutlined />, labelKey: 'nav.social', permission: { module: 'social', action: 'read' } },
  { key: '/audit', icon: <AuditOutlined />, labelKey: 'nav.audit', permission: { module: 'audit', action: 'read' } },
  { key: '/notifications', icon: <BellOutlined />, labelKey: 'nav.notifications', permission: { module: 'notifications', action: 'read' } },
  { key: '/files', icon: <FolderOutlined />, labelKey: 'nav.files', permission: { module: 'files', action: 'read' } },
  { key: '/mobile/devices', icon: <MobileOutlined />, labelKey: 'nav.mobile', permission: { module: 'mobile', action: 'read' } },
  { key: '/collaboration', icon: <CommentOutlined />, labelKey: 'nav.collaboration', permission: { module: 'collaboration', action: 'read' } },
  { key: '/data-management', icon: <DatabaseOutlined />, labelKey: 'nav.data_management', permission: { module: 'data-management', action: 'read' } },
  { key: '/analytics', icon: <ThunderboltOutlined />, labelKey: 'nav.lead_scoring', permission: { module: 'analytics', action: 'read' } },
  { key: '/integration', icon: <ApiOutlined />, labelKey: 'nav.integration', permission: { module: 'integration', action: 'read' } },
  { key: '/admin', icon: <CrownOutlined />, labelKey: 'nav.admin', permission: { module: 'admin', action: 'read' } },
];

function AppLayout() {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const changeLocale = (locale: string) => {
    i18n.changeLanguage(locale);
    localStorage.setItem('wems_crm_locale', locale);
  };

  // Determine the active menu key from the current path
  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !('permission' in item) || !item.permission
      || hasPermission(item.permission.module, item.permission.action)
  );

  const selectedKey = visibleNavItems.find(
    (item) => item.key !== '/' && location.pathname.startsWith(item.key)
  )?.key ?? '/';

  const menuItems = visibleNavItems.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: <Link to={item.key}>{t(item.labelKey)}</Link>,
  }));

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed}>
        <div style={{ height: 32, margin: 16, textAlign: 'center' }}>
          <Title level={5} style={{ color: '#fff', margin: 0 }}>
            {collapsed ? 'CRM' : t('app.name')}
          </Title>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Select
            value={i18n.language}
            onChange={changeLocale}
            style={{ width: 120 }}
            options={[
              { value: 'zh-TW', label: '繁體中文' },
              { value: 'zh-CN', label: '简体中文' },
              { value: 'en-US', label: 'English' },
            ]}
          />
        </Header>
        <Content style={{ margin: 24, padding: 24, background: '#fff', borderRadius: 8, minHeight: 360 }}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/customers" element={<CustomerListPage />} />
            <Route path="/customers/new" element={<CustomerForm />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            <Route path="/customers/:id/edit" element={<CustomerForm />} />
            <Route path="/contacts" element={<ContactListPage />} />
            <Route path="/contacts/new" element={<ContactForm />} />
            <Route path="/contacts/:id" element={<ContactDetailPage />} />
            <Route path="/contacts/:id/edit" element={<ContactForm />} />
            <Route path="/pipelines" element={<PipelineListPage />} />
            <Route path="/pipelines/new" element={<PipelineForm />} />
            <Route path="/pipelines/:id/edit" element={<PipelineForm />} />
            <Route path="/opportunities" element={<OpportunityListPage />} />
            <Route path="/opportunities/kanban" element={<OpportunityKanbanPage />} />
            <Route path="/opportunities/new" element={<OpportunityForm />} />
            <Route path="/opportunities/:id" element={<OpportunityDetailPage />} />
            <Route path="/opportunities/:id/edit" element={<OpportunityForm />} />
            <Route path="/activities" element={<ActivityListPage />} />
            <Route path="/activities/new" element={<ActivityForm />} />
            <Route path="/activities/:id" element={<ActivityDetailPage />} />
            <Route path="/activities/:id/edit" element={<ActivityForm />} />
            <Route path="/quotes" element={<QuoteListPage />} />
            <Route path="/quotes/new" element={<QuoteForm />} />
            <Route path="/quotes/:id" element={<QuoteDetailPage />} />
            <Route path="/quotes/:id/edit" element={<QuoteForm />} />
            <Route path="/orders" element={<OrderListPage />} />
            <Route path="/orders/new" element={<OrderForm />} />
            <Route path="/orders/:id" element={<OrderDetailPage />} />
            <Route path="/orders/:id/edit" element={<OrderForm />} />
            <Route path="/dashboards" element={<DashboardListPage />} />
            <Route path="/dashboards/new" element={<DashboardForm />} />
            <Route path="/dashboards/:id" element={<DashboardViewPage />} />
            <Route path="/dashboards/:id/edit" element={<DashboardForm />} />
            <Route path="/reports" element={<ReportListPage />} />
            <Route path="/reports/new" element={<ReportForm />} />
            <Route path="/reports/:id" element={<ReportDetailPage />} />
            <Route path="/reports/:id/edit" element={<ReportForm />} />
            <Route path="/marketing" element={<CampaignListPage />} />
            <Route path="/marketing/campaigns/new" element={<CampaignForm />} />
            <Route path="/marketing/campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="/marketing/campaigns/:id/edit" element={<CampaignForm />} />
            <Route path="/marketing/analytics" element={<CampaignAnalyticsDashboard />} />
            <Route path="/tickets" element={<TicketListPage />} />
            <Route path="/tickets/new" element={<TicketForm />} />
            <Route path="/tickets/:id" element={<TicketDetailPage />} />
            <Route path="/tickets/:id/edit" element={<TicketForm />} />
            <Route path="/conversations" element={<ConversationManagementPage />} />
            <Route path="/member-messages" element={<MemberMessageManagementPage />} />
            <Route path="/products" element={<ProductListPage />} />
            <Route path="/products/new" element={<ProductForm />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/products/:id/edit" element={<ProductForm />} />
            <Route path="/inventory/new" element={<InventoryTransactionForm />} />
            <Route path="/workflows" element={<WorkflowDefinitionListPage />} />
            <Route path="/workflows/definitions/new" element={<WorkflowDefinitionForm />} />
            <Route path="/workflows/definitions/:id" element={<WorkflowDefinitionDetailPage />} />
            <Route path="/workflows/definitions/:id/edit" element={<WorkflowDefinitionForm />} />
            <Route path="/workflows/instances" element={<WorkflowInstanceListPage />} />
            <Route path="/workflows/instances/new" element={<WorkflowInstanceForm />} />
            <Route path="/workflows/instances/:id" element={<WorkflowInstanceDetailPage />} />
            <Route path="/social" element={<SocialAccountListPage />} />
            <Route path="/social/accounts/new" element={<SocialAccountForm />} />
            <Route path="/social/accounts/:id" element={<SocialAccountDetailPage />} />
            <Route path="/social/accounts/:id/edit" element={<SocialAccountForm />} />
            <Route path="/social/posts" element={<SocialPostListPage />} />
            <Route path="/social/posts/new" element={<SocialPostForm />} />
            <Route path="/social/posts/:id" element={<SocialPostDetailPage />} />
            <Route path="/social/posts/:id/edit" element={<SocialPostForm />} />
            <Route path="/social/messages" element={<SocialMessageListPage />} />
            <Route path="/social/messages/:id" element={<SocialMessageDetailPage />} />
            <Route path="/audit" element={<AuditLogListPage />} />
            <Route path="/audit/logs/:id" element={<AuditLogDetailPage />} />
            <Route path="/audit/configs" element={<AuditConfigListPage />} />
            <Route path="/audit/configs/new" element={<AuditConfigForm />} />
            <Route path="/audit/configs/:id/edit" element={<AuditConfigForm />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/tenants" element={<TenantListPage />} />
            <Route path="/admin/tenants/new" element={<TenantForm />} />
            <Route path="/admin/tenants/:id" element={<TenantDetailPage />} />
            <Route path="/admin/tenants/:id/edit" element={<TenantForm />} />
            <Route path="/admin/system-settings" element={<SystemSettingListPage />} />
            <Route path="/admin/system-settings/new" element={<SystemSettingForm />} />
            <Route path="/admin/system-settings/:id/edit" element={<SystemSettingForm />} />
            <Route path="/admin/tenant-settings" element={<TenantSettingListPage />} />
            <Route path="/admin/tenant-settings/new" element={<TenantSettingForm />} />
            <Route path="/admin/tenant-settings/:id/edit" element={<TenantSettingForm />} />
            <Route path="/admin/notification-preferences" element={<NotificationPrefListPage />} />
            <Route path="/admin/notification-preferences/new" element={<NotificationPrefForm />} />
            <Route path="/admin/notification-preferences/:id/edit" element={<NotificationPrefForm />} />
            <Route path="/admin/branding" element={<BrandingPage />} />
            <Route path="/admin/domains" element={<DomainListPage />} />
            <Route path="/admin/domains/new" element={<DomainForm />} />
            <Route path="/admin/domains/:id/edit" element={<DomainForm />} />
            <Route path="/admin/email-templates" element={<EmailTemplateListPage />} />
            <Route path="/admin/email-templates/new" element={<EmailTemplateForm />} />
            <Route path="/admin/email-templates/:id" element={<EmailTemplatePreview />} />
            <Route path="/admin/email-templates/:id/edit" element={<EmailTemplateForm />} />
            <Route path="/admin/channel-configs" element={<ChannelConfigListPage />} />
            <Route path="/admin/channel-configs/new" element={<ChannelConfigForm />} />
            <Route path="/admin/channel-configs/:id/edit" element={<ChannelConfigForm />} />
            <Route path="/files" element={<FileListPage />} />
            <Route path="/files/shares" element={<FileShareListPage />} />
            <Route path="/mobile/devices" element={<MobileDeviceListPage />} />
            <Route path="/mobile/dashboard" element={<MobileDashboardPage />} />
            <Route path="/mobile" element={<MobileDeviceListPage />} />
            <Route path="/analytics/scoring-rules" element={<ScoringRuleListPage />} />
            <Route path="/analytics/scoring-rules/new" element={<ScoringRuleForm />} />
            <Route path="/analytics/scoring-rules/:id/edit" element={<ScoringRuleForm />} />
            <Route path="/analytics/lead-scores" element={<LeadScoreListPage />} />
            <Route path="/analytics/snapshots" element={<SnapshotListPage />} />
            <Route path="/analytics/forecasts" element={<ForecastListPage />} />
            <Route path="/analytics" element={<ScoringRuleListPage />} />
            <Route path="/integration/configs" element={<IntegrationConfigListPage />} />
            <Route path="/integration/configs/new" element={<IntegrationConfigForm />} />
            <Route path="/integration/configs/:id/edit" element={<IntegrationConfigForm />} />
            <Route path="/integration/oauth-tokens" element={<OAuthTokenListPage />} />
            <Route path="/integration/webhooks" element={<WebhookSubscriptionListPage />} />
            <Route path="/integration/webhooks/new" element={<WebhookSubscriptionForm />} />
            <Route path="/integration/webhooks/:id/edit" element={<WebhookSubscriptionForm />} />
            <Route path="/integration/webhook-events" element={<WebhookEventListPage />} />
            <Route path="/integration" element={<IntegrationConfigListPage />} />
            <Route path="/notifications" element={<NotificationListPage />} />
            <Route path="/notifications/:id" element={<NotificationDetailPage />} />
            <Route path="/collaboration" element={<TeamListPage />} />
            <Route path="/collaboration/teams" element={<TeamListPage />} />
            <Route path="/collaboration/teams/new" element={<TeamForm />} />
            <Route path="/collaboration/teams/:id" element={<TeamDetailPage />} />
            <Route path="/collaboration/teams/:id/edit" element={<TeamForm />} />
            <Route path="/collaboration/shared-views" element={<SharedViewListPage />} />
            <Route path="/collaboration/shared-views/new" element={<SharedViewForm />} />
            <Route path="/collaboration/shared-views/:id/edit" element={<SharedViewForm />} />
            <Route path="/data-management" element={<ImportJobListPage />} />
            <Route path="/data-management/imports" element={<ImportJobListPage />} />
            <Route path="/data-management/imports/new" element={<ImportJobForm />} />
            <Route path="/data-management/imports/:id" element={<ImportJobDetailPage />} />
            <Route path="/data-management/imports/:id/edit" element={<ImportJobForm />} />
            <Route path="/data-management/exports" element={<ExportJobListPage />} />
            <Route path="/data-management/exports/new" element={<ExportJobForm />} />
            <Route path="/data-management/exports/:id" element={<ExportJobDetailPage />} />
            <Route path="/data-management/exports/:id/edit" element={<ExportJobForm />} />
            <Route path="/data-management/dedup-rules" element={<DeduplicationRuleListPage />} />
            <Route path="/data-management/dedup-rules/new" element={<DeduplicationRuleForm />} />
            <Route path="/data-management/dedup-rules/:id" element={<DeduplicationRuleDetailPage />} />
            <Route path="/data-management/dedup-rules/:id/edit" element={<DeduplicationRuleForm />} />
            <Route path="/data-management/dedup-jobs" element={<DeduplicationJobListPage />} />
            <Route path="/data-management/dedup-jobs/:id" element={<DeduplicationJobDetailPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
        <Footer style={{ textAlign: 'center' }}>
          WEMS CRM &copy; {new Date().getFullYear()}
        </Footer>
      </Layout>
    </Layout>
  );
}

function AuthGuard() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Space direction="vertical" align="center">
          <LoadingOutlined spin style={{ fontSize: 32 }} />
          <Typography.Text>正在確認安全登入狀態...</Typography.Text>
        </Space>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ExternalRedirectPage
        targetUrl={EXTERNAL_PORTALS.memberLoginUrl}
        title="轉往會員系統"
        message="會員登入、註冊與帳號頁由中央會員系統處理。"
      />
    );
  }

  return <AppLayout />;
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.memberLoginUrl}
              title="轉往會員系統"
              message="WEMS CRM 不提供本地會員登入頁。"
            />
          }
        />
        <Route
          path="/member/*"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.memberCenterUrl}
              title="轉往會員中心"
              message="會員資料與會員中心由中央會員系統處理。"
            />
          }
        />
        <Route
          path="/account/*"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.memberCenterUrl}
              title="轉往會員中心"
              message="帳號與會員資料由中央會員系統處理。"
            />
          }
        />
        <Route
          path="/profile/*"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.memberCenterUrl}
              title="轉往會員中心"
              message="個人資料由中央會員系統處理。"
            />
          }
        />
        <Route
          path="/register"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.memberLoginUrl}
              title="轉往會員系統"
              message="註冊與登入由中央會員系統處理。"
            />
          }
        />
        <Route
          path="/signup"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.memberLoginUrl}
              title="轉往會員系統"
              message="註冊與登入由中央會員系統處理。"
            />
          }
        />
        <Route
          path="/billing/*"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.paymentUrl}
              title="轉往付款點數系統"
              message="付款、點數與帳單頁由外部付款系統處理。"
            />
          }
        />
        <Route
          path="/payment/*"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.paymentUrl}
              title="轉往付款點數系統"
              message="付款與點數由外部付款系統處理。"
            />
          }
        />
        <Route
          path="/points/*"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.paymentUrl}
              title="轉往付款點數系統"
              message="點數資料由外部付款系統處理。"
            />
          }
        />
        <Route
          path="/checkout/*"
          element={
            <ExternalRedirectPage
              targetUrl={EXTERNAL_PORTALS.paymentUrl}
              title="轉往付款點數系統"
              message="結帳與付款由外部付款系統處理。"
            />
          }
        />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/*" element={<AuthGuard />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
